import React, { useCallback, useContext, useMemo } from 'react';

import { useCurrentUser } from 'Auth';
import clsx from 'clsx';
import trim from 'lodash/trim';

import {
  Divider,
  SearchInput,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  makeStyles,
  VirtualizedList,
  Typography,
  SortIcon,
  PeopleIcon,
} from '@gogame/components';
import { useMountedState } from '@gogame/hooks-ts';

import { PresetsContext } from '../PresetsContext';
import FolderIcon from '../components/FolderIcon';
import GroupActions from '../components/GroupActions';
import PresetActions from '../components/PresetActions';
import { useCreatePreset } from '../hooks/useCreatePreset';

export const getOptionLabel = ({ title, isPublic }) => (
  <Stack direction={'row'} spacing={1} alignItems={'center'}>
    <Typography component={'span'} noWrap={true}>
      {title}
    </Typography>
    {isPublic ? <PeopleIcon sx={{ fontSize: 14 }} color={'action'} /> : null}
  </Stack>
);

export const searchPresets = ({ presets, searchQuery }) =>
  presets.reduce((prev, cur) => {
    let hasMatch = (cur.title || cur.name)
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase());

    if (hasMatch) {
      prev.push(cur);
    } else if (cur?.children) {
      const matchedPresets = cur.children.filter((pr) =>
        pr.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (matchedPresets.length) {
        prev.push({ ...cur, children: matchedPresets });
      }
    }
    return prev;
  }, []);
const useStyles = makeStyles((theme) => ({
  selectedMenuItem: { background: theme.palette.action.selected },
}));

const PresetsList = ({
  withAddPreset = false,
  setOrderBy,
  onManageGroups,
  onClose,
  presets,
  isManageGroups,
  groupActions,
  groupActionLimit,
  onItemClick,
  withOwnerName,
}) => {
  const user = useCurrentUser();
  const [searchQuery, setSearchQuery] = useMountedState('');
  const {
    orderBy,
    activePreset,
    screenData,
    titleDialogUpdate,
    labelReset,
    labelUpdate,
    presetKey,
    isAddPresetWithGroup,
    titleDialogAdd,
  } = useContext(PresetsContext);
  const [createChildComponent, openCreatePresetSidePage] = useCreatePreset({
    presetKey,
    isAddPresetWithGroup,
    screenData,
    titleDialogAdd,
  });
  const classes = useStyles();

  const filteredPresets = useMemo(() => {
    if (searchQuery) {
      return searchPresets({ presets, searchQuery });
    }
    return presets;
  }, [JSON.stringify(presets), searchQuery]);

  const expandedIds = useMemo(() => {
    //"Ungrouped" group id is 0
    let result = [0];

    if (activePreset?.groupId) {
      result.push(activePreset?.groupId);
    }
    return result;
  }, [activePreset]);

  const getSecondaryLabel = useCallback(
    (option) => withOwnerName && option?.user?.name,
    [withOwnerName]
  );

  const getIsSelected = useCallback(
    (option, { isGroupItem }) => !isGroupItem && activePreset?.id === option.id,
    [activePreset?.id]
  );

  const handleCreatePreset = async () => {
    await openCreatePresetSidePage();
  };

  return (
    <React.Fragment>
      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        spacing={2}
        style={{ margin: '12px' }}
      >
        <SearchInput
          placeholder={'Search'}
          value={searchQuery}
          variant={'filled'}
          onChange={(_e, value) => setSearchQuery(trim(value))}
          style={{ flex: 1 }}
        />
        {withAddPreset ? (
          <Button add onClick={handleCreatePreset} variant={'outlined'}>
            Preset
          </Button>
        ) : null}
        {isManageGroups ? (
          <Button onClick={onManageGroups}>Manage groups</Button>
        ) : null}
        <Menu anchor={<IconButton icon={<SortIcon />} />}>
          <MenuItem
            onClick={() => {
              setOrderBy('LastUsage');
            }}
            className={clsx({
              [classes.selectedMenuItem]: orderBy === 'LastUsage',
            })}
          >
            Last used
          </MenuItem>
          <MenuItem
            onClick={() => {
              setOrderBy('Title');
            }}
            className={clsx({
              [classes.selectedMenuItem]: orderBy === 'Title',
            })}
          >
            Name
          </MenuItem>
        </Menu>
      </Stack>
      {filteredPresets?.length ? <Divider /> : null}
      <Stack flex={'1 0 auto'}>
        <VirtualizedList
          expandedIds={expandedIds}
          data={filteredPresets}
          isExpanded={() => !!searchQuery}
          secondaryAction={(option, { isGroupItem }) =>
            isGroupItem ? (
              <GroupActions
                {...option}
                actions={groupActions}
                limit={groupActionLimit}
              />
            ) : (
              <PresetActions
                {...option}
                onClose={onClose}
                screenData={screenData}
                titleDialogUpdate={titleDialogUpdate}
                isOwner={option?.user?.id === user.id}
                isActive={activePreset?.id === option.id}
                labelReset={labelReset}
                labelUpdate={labelUpdate}
              />
            )
          }
          icon={FolderIcon}
          isSelected={getIsSelected}
          getOptionSecondaryLabel={getSecondaryLabel}
          getOptionLabel={getOptionLabel}
          onItemClick={onItemClick}
          button
          gutterLabel
        />
      </Stack>
      {createChildComponent}
    </React.Fragment>
  );
};

export default PresetsList;
